import { useCallback, useContext, useEffect, useRef, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Grid,
  Menu,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import SortIcon from '@mui/icons-material/Sort'

import { ClothingDataDB } from 'models'
import UploadCard from 'components/upload_card'
import { get_crediting_uploads_completed_paginated } from 'api/clothes_service'
import { useDocumentTitle } from 'hooks'
import { UnauthorisedError } from 'errors'
import { useSearchParams } from 'react-router-dom'
import BackToTop from 'components/back_to_top'
import { UserContext } from 'base'

const UploadsContent = () => {
  const [searchParams] = useSearchParams()

  const [clothingUploads, setClothingUploads] =
    useState<Array<ClothingDataDB>>()
  const [next, setNext] = useState<string | null>(null)
  const [nextId, setNextId] = useState<string | null>(null)
  const [error, setError] = useState<string>()
  const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false)
  const observerTarget = useRef<HTMLElement>(null)
  const user = useContext(UserContext)

  const populateClothingUploads = useCallback(async () => {
    setError(undefined)
    setClothingUploads(undefined)
    try {
      const clothingUploadsData =
        await get_crediting_uploads_completed_paginated(
          searchParams.get('sort'),
          null,
          null,
          null,
          null,
          searchParams.get('name'),
          searchParams.get('email')
        )
      setClothingUploads(clothingUploadsData.uploads)
      setNext(clothingUploadsData.next)
      setNextId(clothingUploadsData.next_id)
    } catch (e: any) {
      if (e instanceof UnauthorisedError) {
        return user.setLoggedOut()
      }

      setError(e.message)
    }
  }, [searchParams, user])

  const getMoreUploads = useCallback(async () => {
    if (isLoadingMore) return
    setIsLoadingMore(true)
    // TODO: Refactor?
    // TODO: Error handling??
    try {
      const clothingUploadsData =
        await get_crediting_uploads_completed_paginated(
          searchParams.get('sort'),
          null,
          null,
          next,
          nextId,
          searchParams.get('name'),
          searchParams.get('email')
        )
      setNext(clothingUploadsData.next)
      setNextId(clothingUploadsData.next_id)
      setClothingUploads((existingUploads) =>
        existingUploads !== undefined
          ? [...existingUploads, ...clothingUploadsData.uploads]
          : clothingUploadsData.uploads
      )
    } catch (e: any) {
      return
    }
    setIsLoadingMore(false)
  }, [next, nextId, searchParams, isLoadingMore])

  useEffect(() => {
    let observerRefValue: HTMLElement | null = null

    // Reference: https://blog.logrocket.com/3-ways-implement-infinite-scroll-react/
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          getMoreUploads()
        }
      },
      { threshold: 1 }
    )

    if (observerTarget.current) {
      observer.observe(observerTarget.current)
      observerRefValue = observerTarget.current
    }

    return () => {
      if (observerRefValue !== null) {
        observer.unobserve(observerRefValue)
      }
    }
  }, [observerTarget, getMoreUploads])

  useEffect(() => {
    populateClothingUploads()
  }, [populateClothingUploads])

  if (error !== undefined) {
    return (
      <Alert severity="error">
        <Typography variant="h5">{error}</Typography>
      </Alert>
    )
  }

  if (clothingUploads === undefined) {
    return (
      <Box display="flex" justifyContent="center" pt={2}>
        <Stack display="flex" alignItems="center" spacing={1}>
          <CircularProgress />
          <Typography variant="body1">Loading data...</Typography>
        </Stack>
      </Box>
    )
  }

  if (clothingUploads.length === 0) {
    return <Typography>No uploads.</Typography>
  }

  return (
    <>
      {clothingUploads.map((clothingData) => (
        <UploadCard
          key={clothingData.id}
          clothingData={clothingData}
          link={`/completed/${clothingData.id}`}
        ></UploadCard>
      ))}
      {isLoadingMore ? (
        <Stack display="flex" alignItems="center" spacing={1}>
          <CircularProgress />
        </Stack>
      ) : (
        (next !== null || nextId !== null) && <Box ref={observerTarget}></Box>
      )}
    </>
  )
}

enum SortMethod {
  APPROVAL_DATE = 'Approval date (newest first)',
  UPLOADED_DATE = 'Uploaded date (newest first)',
  COLLECTION_DATE = 'Collection date (newest first)',
}

const SortButton = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const sortButtonRef = useRef<HTMLButtonElement>(null)
  const [searchParams, setSearchParams] = useSearchParams()
  const [selectedMethod, setSelectedMethod] = useState(
    searchParams.get('sort') ?? SortMethod.APPROVAL_DATE
  )

  const handleSortMethodClick = (sortMethod: SortMethod) => {
    setSelectedMethod(sortMethod)
    setIsMenuOpen(false)
    setSearchParams((searchParams) => {
      searchParams.set('sort', sortMethod)
      return searchParams
    })
  }

  return (
    <>
      <Button
        id="sort-button"
        startIcon={<SortIcon />}
        onClick={() => {
          setIsMenuOpen(true)
        }}
        ref={sortButtonRef}
      >
        Sort by
      </Button>
      <Menu
        open={isMenuOpen}
        onClose={() => setIsMenuOpen(false)}
        anchorEl={sortButtonRef.current}
        MenuListProps={{
          'aria-labelledby': 'sort-button',
        }}
      >
        {Object.values(SortMethod).map((sortMethod) => (
          <MenuItem
            key={sortMethod}
            onClick={() => handleSortMethodClick(sortMethod)}
            selected={selectedMethod === sortMethod}
          >
            {sortMethod}
          </MenuItem>
        ))}
      </Menu>
    </>
  )
}

const SearchBar = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const [nameQuery, setNameQuery] = useState<string>(
    searchParams.get('name') ?? ''
  )
  const [emailQuery, setEmailQuery] = useState<string>(
    searchParams.get('email') ?? ''
  )

  return (
    <Box
      component="form"
      onSubmit={async (event) => {
        event.preventDefault()
        setSearchParams((searchParams) => {
          if (nameQuery) {
            searchParams.set('name', nameQuery)
          } else {
            searchParams.delete('name')
          }
          if (emailQuery) {
            searchParams.set('email', emailQuery)
          } else {
            searchParams.delete('email')
          }
          return searchParams
        })
      }}
    >
      <Grid container gap={2} justifyContent="flex-end">
        <Grid item xs={12} sm={12} md>
          <TextField
            fullWidth
            label="Name"
            value={nameQuery}
            onChange={(event) => setNameQuery(event.target.value)}
            size="small"
          />
        </Grid>
        <Grid item xs={12} sm={12} md>
          <TextField
            fullWidth
            label="Email"
            value={emailQuery}
            onChange={(event) => setEmailQuery(event.target.value)}
            size="small"
          />
        </Grid>
        <Grid item xs="auto">
          <Button variant="contained" type="submit">
            Search
          </Button>
        </Grid>
      </Grid>
    </Box>
  )
}

const SortAndSearch = () => {
  return (
    <Grid container gap={2}>
      <Grid item xs={12} md="auto">
        <SortButton />
      </Grid>
      <Grid item xs>
        <SearchBar />
      </Grid>
    </Grid>
  )
}

const CompletedUploads = () => {
  useDocumentTitle('Completed Uploads')
  return (
    <Stack spacing={2}>
      <Typography variant="h3">{'Completed Uploads'}</Typography>
      <SortAndSearch />
      <UploadsContent />
      <BackToTop />
    </Stack>
  )
}

export default CompletedUploads
